import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type {
  CreateTeamRequest,
  PartialWriteDetails,
  TeamMember,
  TeamMemberIdentities,
} from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { ApiCallError, LocalCliError } from "../../lib/errors.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import {
  colors,
  renderJson,
  resolveOutputMode,
} from "../../lib/output.js";

export class TeamAddCommand extends M8tCommand {
  static paths = [["team", "add"]];
  static usage = Command.Usage({
    description: "Add a new team member with one or more channel identities.",
    examples: [
      [
        "Add with Telegram",
        "$0 team add idabest --display \"Ilan Dabest\" --telegram 88112233",
      ],
      [
        "Add with multiple identities",
        "$0 team add idabest --display \"Ilan Dabest\" --telegram 88112233 --slack U01ILA",
      ],
    ],
  });

  handle = Option.String();
  display = Option.String("--display", { required: true });
  telegram = Option.String("--telegram");
  slack = Option.String("--slack");
  teams = Option.String("--teams");
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const identities: TeamMemberIdentities = {};
    if (this.telegram) identities.telegram = this.telegram;
    if (this.slack) identities.slack = this.slack;
    if (this.teams) identities.teams = this.teams;

    if (Object.keys(identities).length === 0) {
      throw new LocalCliError({
        code: "USAGE",
        message: "at least one of --telegram, --slack, --teams is required.",
        hint: "Example: m8t team add idabest --display \"Ilan\" --telegram 88112233",
      });
    }

    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });
    const body: CreateTeamRequest = {
      handle: this.handle,
      displayName: this.display,
      identities,
    };

    let member: TeamMember;
    try {
      member = await apiCall<TeamMember>(
        { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
        { method: "POST", path: "/api/team", body },
        (msg) => this.context.stderr.write(msg + "\n"),
      );
    } catch (e) {
      // Partial-write failures need a special pretty rendering — show
      // which channels succeeded + remediation hint pointing at the
      // missing identity.
      if (e instanceof ApiCallError) {
        const details = (e.envelope.details as Record<string, unknown> | undefined) ?? {};
        if (details.reason === "table_storage_partial_write" && mode !== "json") {
          const partial = details.partial as PartialWriteDetails | undefined;
          if (partial) {
            const created = Object.keys(partial.created).join(", ") || "(none)";
            this.context.stderr.write(
              colors.error("error:") +
                ` partial write — ${created} succeeded, ${partial.failed.channel} failed.\n`,
            );
            this.context.stderr.write(
              colors.hint("  hint:") +
                ` run 'm8t team show ${this.handle}' to verify, then 'm8t team add-identity ${this.handle} --${partial.failed.channel} ${partial.failed.channelUserId}' to retry.\n`,
            );
            return 1;
          }
        }
      }
      throw e;
    }

    if (mode === "json") {
      this.context.stdout.write(renderJson(member) + "\n");
      return 0;
    }

    const channelsCreated = Object.keys(member.identities).join(", ");
    this.context.stdout.write(
      `${colors.success("✓")} created ${colors.field(member.handle)} (${member.displayName}) with ${channelsCreated} identit${Object.keys(member.identities).length === 1 ? "y" : "ies"}.\n`,
    );
    return 0;
  }
}
